import * as icons from 'icons';
import { AlertSeverity } from 'modules/alerts/state';

import type { ToastOptions, ToastSeverity } from '@natter-co/design-system';
import type { AnyAction } from '@reduxjs/toolkit';
import type { TFunction } from 'i18next';
import type { SnackBarAlertData } from 'modules/alerts/state';

// A plain object, so the name lookup is not a computed access on the module namespace, which lint cannot validate.
const iconsByName: typeof icons = icons;

/**
 * Info maps to neutral, not DS `info`: the legacy Info snackbar has no icon and reads as a plain
 * notice, and most of the 16 Info dispatches are confirmations ("Copied to clipboard").
 */
const SEVERITY: Record<AlertSeverity, ToastSeverity> = {
  [AlertSeverity.Success]: 'success',
  [AlertSeverity.Info]: 'neutral',
  [AlertSeverity.Warning]: 'warning',
  [AlertSeverity.Error]: 'error',
};

/**
 * Maps a Redux snackbar alert onto the Design System 2.0 toast API.
 *
 * TODO(HNS-264): temporary, like `Alert/toDsProps`. It exists so the visual refresh is gated in
 * `AlertsContainer` instead of at the 125 dispatch sites; retiring the flag means deleting the
 * legacy snackbar and letting call sites speak the toast vocabulary directly.
 *
 * Every field of `SnackBarAlertData` has a toast counterpart, so unlike `toDsProps` this never
 * refuses.
 */
export const toToastOptions = (
  { severity, message }: SnackBarAlertData,
  dispatch: (action: AnyAction) => void
): ToastOptions => {
  const isAppAlertMessage = typeof message === 'object';
  const { action, actionLabel, icon, timeout } = isAppAlertMessage ? message : {};
  const Icon = icon ? iconsByName[icon] : undefined;

  return {
    description: isAppAlertMessage ? message.message : message,
    severity: SEVERITY[severity],
    // Unset falls to the DS defaults: 5s like the legacy snackbar, 8s with an action, and an error
    // stays until it is closed. An alert's own timeout still wins.
    timeout,
    icon: Icon && <Icon fontSize="inherit" />,
    action: actionLabel
      ? {
          label: actionLabel,
          onClick: () => {
            if (action) {
              dispatch(action);
            }
          },
        }
      : undefined,
  };
};

/**
 * Maps a backend error onto an error toast, in place of the legacy full-width `ApiErrorAlert` ribbon.
 *
 * The ribbon never shows the alert's own message, only the generic copy with an inline help-centre
 * link, so the toast says the same thing and turns the link into its action. It stays until closed,
 * as the ribbon does. The ribbon's error-code row is not carried over: every dispatch passes a plain
 * string, so it never renders.
 */
export const toBackendErrorToastOptions = (t: TFunction<'common'>): ToastOptions => {
  const supportUrl = window._env_.VITE_APP_INTERCOM_URL;

  return {
    description: t('apiErrorToast.description'),
    severity: 'error',
    timeout: 0,
    action: supportUrl
      ? {
          label: t('apiErrorToast.contactSupport'),
          onClick: () => window.open(supportUrl, '_blank', 'noopener,noreferrer'),
        }
      : undefined,
  };
};
